import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarIcon from '@/shared/assets/icons/calendar.svg';
import ClockIcon from '@/shared/assets/icons/clock.svg';
import ChevronIcon from '@/shared/assets/icons/chevron.svg';
import CheckIcon from '@/shared/assets/icons/check-m.svg';
import { Portal } from '@/shared/ui/Portal/Portal';
import s from './DateTimeInput.module.scss';
import {
  Mode,
  pad,
  parseValue,
  formatDateValue,
  formatTimeValue,
  formatDateTimeValue,
  buildWeekDays,
  monthNames,
  MIN_YEAR,
  MAX_YEAR,
} from './helpers';

interface DateTimeInputProps {
  mode: Mode;
  label?: string;
  value?: string;
  placeholder?: string;
  name?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  error?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onBlur?: () => void;
  renderTrigger?: (onClick: () => void, value: string, isOpen: boolean) => React.ReactNode;
}

const maskByMode: Record<Mode, string> = {
  date: 'дд.мм.гггг',
  time: 'чч:мм',
  datetime: 'дд.мм.гггг чч:мм',
  range: '',
};

const typeByMode: Record<Mode, string> = {
  date: 'date',
  time: 'time',
  datetime: 'datetime-local',
  range: 'hidden',
};

export const DateTimeInput = ({
  mode,
  label,
  value,
  placeholder,
  name,
  min,
  max,
  disabled,
  error,
  onChange,
  onClear,
  onBlur,
  renderTrigger,
}: DateTimeInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updateCoords = useCallback(() => {
    const triggerRect = wrapperRef.current?.getBoundingClientRect();
    if (!triggerRect) return;

    const scrollX = window.scrollX ?? document.documentElement.scrollLeft;
    const scrollY = window.scrollY ?? document.documentElement.scrollTop;

    let popupWidth = 430;
    if (popupRef.current) {
      popupWidth = popupRef.current.getBoundingClientRect().width || popupWidth;
    } else {
      if (mode === 'date') popupWidth = 280;
      else if (mode === 'time') popupWidth = 150;
      else if (mode === 'range') popupWidth = 560;
    }

    const left = triggerRect.right + scrollX - popupWidth;
    const safeLeft = Math.max(16, left);

    setCoords({
      top: triggerRect.bottom + scrollY + 8,
      left: safeLeft,
    });
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;
    updateCoords();

    const timer = setTimeout(() => {
      updateCoords();
    }, 0);

    const handleScroll = () => updateCoords();
    window.addEventListener('resize', handleScroll);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateCoords]);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);

  const [isLeftMonthOpen, setIsLeftMonthOpen] = useState(false);
  const [isLeftYearOpen, setIsLeftYearOpen] = useState(false);
  const [isRightMonthOpen, setIsRightMonthOpen] = useState(false);
  const [isRightYearOpen, setIsRightYearOpen] = useState(false);

  const [rangeLeftViewDate, setRangeLeftViewDate] = useState<Date>(new Date());
  const [rangeRightViewDate, setRangeRightViewDate] = useState<Date>(new Date());

  const parsedDate = useMemo(() => parseValue(value, mode), [mode, value]);
  const initialDate = parsedDate ?? new Date();

  const [viewDate, setViewDate] = useState<Date>(initialDate);
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsedDate);
  const [selectedHour, setSelectedHour] = useState<number>(parsedDate?.getHours() ?? 0);
  const [selectedMinute, setSelectedMinute] = useState<number>(parsedDate?.getMinutes() ?? 0);

  const [rangeStep, setRangeStep] = useState<'start' | 'end'>('start');
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [rangeHover, setRangeHover] = useState<Date | null>(null);

  const minDateParsed = useMemo(
    () => (min ? parseValue(min, mode === 'datetime' ? 'datetime' : mode) : null),
    [min, mode],
  );
  const maxDateParsed = useMemo(
    () => (max ? parseValue(max, mode === 'datetime' ? 'datetime' : mode) : null),
    [max, mode],
  );

  const isDayDisabled = useCallback(
    (day: Date) => {
      if (mode === 'time') return false;

      const d = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();

      if (minDateParsed) {
        const m = new Date(minDateParsed.getFullYear(), minDateParsed.getMonth(), minDateParsed.getDate()).getTime();
        if (d < m) return true;
      }
      if (maxDateParsed) {
        const m = new Date(maxDateParsed.getFullYear(), maxDateParsed.getMonth(), maxDateParsed.getDate()).getTime();
        if (d > m) return true;
      }
      return false;
    },
    [minDateParsed, maxDateParsed, mode],
  );

  const icon = useMemo(() => (mode === 'time' ? <ClockIcon /> : <CalendarIcon />), [mode]);
  const type = typeByMode[mode];
  const appliedPlaceholder = isFocused ? maskByMode[mode] : '';
  const showCustomPlaceholder = !isFocused && !value;

  const minProp = useMemo(() => {
    if (mode === 'date' || mode === 'datetime') {
      return min ?? `${MIN_YEAR}-01-01`;
    }
    return min;
  }, [min, mode]);

  const maxProp = useMemo(() => {
    if (mode === 'date' || mode === 'datetime') {
      return max ?? `${MAX_YEAR}-12-31`;
    }
    return max;
  }, [max, mode]);

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const clampYear = (raw: string) => {
    if (!raw) return raw;
    if (mode === 'date') {
      const [y, m = '', d = ''] = raw.split('-');
      if (!y || y.length < 4) return raw;
      const year = Number(y);
      if (Number.isNaN(year)) return raw;
      const clamped = Math.min(Math.max(year, MIN_YEAR), MAX_YEAR).toString().padStart(4, '0');
      return `${clamped}-${m}-${d}`;
    }
    if (mode === 'datetime') {
      const [datePart, timePart = '00:00'] = raw.split('T');
      if (!datePart) return raw;
      const [y, m = '', d = ''] = datePart.split('-');
      if (!y || y.length < 4) return raw;
      const year = Number(y);
      if (Number.isNaN(year)) return raw;
      const clamped = Math.min(Math.max(year, MIN_YEAR), MAX_YEAR).toString().padStart(4, '0');
      const timeSuffix = timePart ? `T${timePart}` : '';
      return `${clamped}-${m}-${d}${timeSuffix}`;
    }
    return raw;
  };

  const clampToRange = useCallback(
    (raw: string) => {
      if (!raw) return raw;
      const d = parseValue(raw, mode);
      if (!d) return raw;

      if (minDateParsed && d < minDateParsed) {
        if (mode === 'date') return formatDateValue(minDateParsed);
        if (mode === 'time') return formatTimeValue(minDateParsed);
        return formatDateTimeValue(minDateParsed);
      }
      if (maxDateParsed && d > maxDateParsed) {
        if (mode === 'date') return formatDateValue(maxDateParsed);
        if (mode === 'time') return formatTimeValue(maxDateParsed);
        return formatDateTimeValue(maxDateParsed);
      }
      return raw;
    },
    [maxDateParsed, minDateParsed, mode],
  );

  useEffect(() => {
    if (value) {
      const clamped = clampToRange(value);
      if (clamped !== value) {
        onChange?.(clamped);
      }
    }
  }, [clampToRange, onChange, value]);

  const openPicker = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const closeAll = () => {
    setIsOpen(false);
    setIsMonthOpen(false);
    setIsYearOpen(false);
    setIsLeftMonthOpen(false);
    setIsLeftYearOpen(false);
    setIsRightMonthOpen(false);
    setIsRightYearOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const path = e.composedPath();
      if (popupRef.current && path.includes(popupRef.current)) return;
      if (wrapperRef.current && path.includes(wrapperRef.current)) return;
      closeAll();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAll();
      }
    };
    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  useEffect(() => {
    if (parsedDate) {
      setSelectedDate(parsedDate);
      setViewDate(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
      setSelectedHour(parsedDate.getHours());
      setSelectedMinute(parsedDate.getMinutes());
    }
  }, [parsedDate]);

  useEffect(() => {
    if (mode === 'range') {
      if (value) {
        const [s, e] = value.split('|');
        setRangeStart(parseValue(s, 'date'));
        setRangeEnd(parseValue(e, 'date'));
      } else {
        setRangeStart(null);
        setRangeEnd(null);
      }
    }
  }, [value, mode]);

  useEffect(() => {
    if (isOpen && mode === 'range') {
      let left = new Date();
      left.setHours(0, 0, 0, 0);
      left.setDate(1);

      let right = new Date(left.getFullYear(), left.getMonth() + 1, 1);

      if (value) {
        const [s, e] = value.split('|');
        const ds = parseValue(s, 'date');
        const de = parseValue(e, 'date');
        if (ds && de) {
          left = new Date(ds.getFullYear(), ds.getMonth(), 1);
          right = new Date(de.getFullYear(), de.getMonth(), 1);
          if (left.getTime() === right.getTime()) {
            right = new Date(left.getFullYear(), left.getMonth() + 1, 1);
          }
          setRangeStart(ds);
          setRangeEnd(de);
        } else {
          setRangeStart(null);
          setRangeEnd(null);
        }
        setRangeStep('end');
      } else {
        setRangeStart(null);
        setRangeEnd(null);
        setRangeStep('start');
      }
      setRangeHover(null);
      setRangeLeftViewDate(left);
      setRangeRightViewDate(right);
    }
  }, [isOpen, mode, value]);

  const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const endOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
  const startWeekday = (startOfMonth.getDay() + 6) % 7;

  const days: Date[] = useMemo(() => {
    const result: Date[] = [];
    const prevDays = startWeekday;
    for (let i = prevDays; i > 0; i--) {
      result.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1 - i));
    }
    for (let d = 1; d <= endOfMonth.getDate(); d++) {
      result.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
    }
    while (result.length < 42) {
      const last = result[result.length - 1];
      result.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1));
    }
    return result;
  }, [endOfMonth, startWeekday, viewDate]);

  const isSameDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };

  const handleDaySelect = (day: Date) => {
    if (isDayDisabled(day)) return;

    const withTime = new Date(day);
    withTime.setHours(selectedHour, selectedMinute, 0, 0);
    setSelectedDate(withTime);

    if (mode === 'date') {
      onChange?.(formatDateValue(withTime));
    }
    if (mode === 'datetime') {
      onChange?.(formatDateTimeValue(withTime));
    }
  };

  const changeMonth = (delta: number) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const applyValue = () => {
    let next: Date | null = null;
    if (mode === 'date') {
      const base = selectedDate ?? viewDate;
      next = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0);
    } else if (mode === 'time') {
      const base = selectedDate ?? new Date();
      next = new Date(base);
      next.setHours(selectedHour, selectedMinute, 0, 0);
    } else {
      const baseDay = selectedDate ?? viewDate;
      next = new Date(baseDay.getFullYear(), baseDay.getMonth(), baseDay.getDate(), selectedHour, selectedMinute, 0, 0);
    }

    if (minDateParsed && next < minDateParsed) {
      next = new Date(minDateParsed);
    }
    if (maxDateParsed && next > maxDateParsed) {
      next = new Date(maxDateParsed);
    }

    if (mode === 'date') {
      onChange?.(formatDateValue(next));
    } else if (mode === 'time') {
      onChange?.(formatTimeValue(next));
    } else {
      onChange?.(formatDateTimeValue(next));
    }

    closeAll();
    onBlur?.();
  };

  const getYearOptions = (targetDate: Date) => {
    const current = targetDate.getFullYear();
    const years: number[] = [];
    for (let y = current - 5; y <= current + 5; y++) {
      years.push(y);
    }
    return years;
  };

  const yearOptions = useMemo(() => getYearOptions(viewDate), [viewDate]);

  const handleLeftChange = (d: Date) => {
    setRangeLeftViewDate(d);
    const lIdx = d.getFullYear() * 12 + d.getMonth();
    const rIdx = rangeRightViewDate.getFullYear() * 12 + rangeRightViewDate.getMonth();
    if (lIdx >= rIdx) {
      setRangeRightViewDate(new Date(d.getFullYear(), d.getMonth() + 1, 1));
    }
  };

  const handleRightChange = (d: Date) => {
    setRangeRightViewDate(d);
    const lIdx = rangeLeftViewDate.getFullYear() * 12 + rangeLeftViewDate.getMonth();
    const rIdx = d.getFullYear() * 12 + d.getMonth();
    if (rIdx <= lIdx) {
      setRangeLeftViewDate(new Date(d.getFullYear(), d.getMonth() - 1, 1));
    }
  };

  const changeRangeMonth = (isLeft: boolean, delta: number) => {
    if (isLeft) {
      const next = new Date(rangeLeftViewDate.getFullYear(), rangeLeftViewDate.getMonth() + delta, 1);
      handleLeftChange(next);
    } else {
      const next = new Date(rangeRightViewDate.getFullYear(), rangeRightViewDate.getMonth() + delta, 1);
      handleRightChange(next);
    }
  };

  const selectRangeMonth = (isLeft: boolean, monthIdx: number) => {
    if (isLeft) {
      handleLeftChange(new Date(rangeLeftViewDate.getFullYear(), monthIdx, 1));
      setIsLeftMonthOpen(false);
    } else {
      handleRightChange(new Date(rangeRightViewDate.getFullYear(), monthIdx, 1));
      setIsRightMonthOpen(false);
    }
  };

  const selectRangeYear = (isLeft: boolean, year: number) => {
    if (isLeft) {
      handleLeftChange(new Date(year, rangeLeftViewDate.getMonth(), 1));
      setIsLeftYearOpen(false);
    } else {
      handleRightChange(new Date(year, rangeRightViewDate.getMonth(), 1));
      setIsRightYearOpen(false);
    }
  };

  const selectMonth = (monthIdx: number) => {
    setViewDate((prev) => new Date(prev.getFullYear(), monthIdx, 1));
    setIsMonthOpen(false);
  };

  const selectYear = (year: number) => {
    setViewDate((prev) => new Date(year, prev.getMonth(), 1));
    setIsYearOpen(false);
  };

  const isOtherMonth = (day: Date) => day.getMonth() !== viewDate.getMonth();

  const handleTimeSelect = (hour: number, minute?: number) => {
    if (typeof minute === 'number') {
      setSelectedMinute(minute);
      if (mode === 'time') {
        const base = new Date();
        base.setHours(selectedHour, minute, 0, 0);
        onChange?.(formatTimeValue(base));
      }
      if (mode === 'datetime' && selectedDate) {
        const combined = new Date(selectedDate);
        combined.setHours(selectedHour, minute, 0, 0);
        onChange?.(formatDateTimeValue(combined));
      }
      return;
    }

    setSelectedHour(hour);

    if (mode === 'time') {
      const base = new Date();
      base.setHours(hour, selectedMinute, 0, 0);
      onChange?.(formatTimeValue(base));
    }
    if (mode === 'datetime' && selectedDate) {
      const combined = new Date(selectedDate);
      combined.setHours(hour, selectedMinute, 0, 0);
      onChange?.(formatDateTimeValue(combined));
    }
  };

  const displayTime = `${pad(selectedHour)}:${pad(selectedMinute)}`;
  const timeOnly = mode === 'time';

  const buildMonthDays = useCallback((monthStart: Date) => {
    const startOfM = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
    const endOfM = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const startWeekday = (startOfM.getDay() + 6) % 7;

    const result: Date[] = [];
    for (let i = startWeekday; i > 0; i--) {
      result.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), 1 - i));
    }
    for (let d = 1; d <= endOfM.getDate(); d++) {
      result.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), d));
    }
    while (result.length < 42) {
      const last = result[result.length - 1];
      result.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1));
    }
    return result;
  }, []);

  const rangeLeftDays = useMemo(() => buildMonthDays(rangeLeftViewDate), [buildMonthDays, rangeLeftViewDate]);
  const rangeRightDays = useMemo(() => buildMonthDays(rangeRightViewDate), [buildMonthDays, rangeRightViewDate]);

  const inRange = useCallback(
    (day: Date) => {
      if (!rangeStart) return false;
      const end = rangeHover || rangeEnd;
      if (!end) return false;
      const d = day.getTime();
      const s = rangeStart.getTime();
      const e = end.getTime();
      return d >= Math.min(s, e) && d <= Math.max(s, e);
    },
    [rangeStart, rangeEnd, rangeHover],
  );

  const isRangeStart = useCallback(
    (day: Date) => {
      if (!rangeStart) return false;
      return isSameDay(day, rangeStart);
    },
    [rangeStart],
  );

  const isRangeEnd = useCallback(
    (day: Date) => {
      if (!rangeEnd) return false;
      return isSameDay(day, rangeEnd);
    },
    [rangeEnd],
  );

  const handleRangeDayClick = (day: Date) => {
    if (rangeStep === 'start') {
      setRangeStart(day);
      setRangeEnd(null);
      setRangeHover(null);
      setRangeStep('end');
    } else {
      if (!rangeStart) return;
      if (day.getTime() < rangeStart.getTime()) {
        setRangeEnd(rangeStart);
        setRangeStart(day);
      } else if (isSameDay(day, rangeStart)) {
        setRangeStart(null);
        setRangeEnd(null);
        setRangeHover(null);
        setRangeStep('start');
        return;
      } else {
        setRangeEnd(day);
      }
      setRangeHover(null);
    }
  };

  const getDayProps = useCallback(
    (day: Date, currentMonthDate: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayNorm = new Date(day);
      dayNorm.setHours(0, 0, 0, 0);

      const isToday = isSameDay(dayNorm, today);
      const isOtherMonth = dayNorm.getMonth() !== currentMonthDate.getMonth();
      const isPast = dayNorm.getTime() < today.getTime();
      const disabledDay = isDayDisabled(dayNorm);

      let inR = false;
      let isStart = false;
      let isEnd = false;
      let isSelected = false;

      if (mode === 'range') {
        isSelected = isSameDay(dayNorm, rangeStart) || isSameDay(dayNorm, rangeEnd);
        if (rangeStart) {
          const end = rangeHover || rangeEnd;
          if (!end) {
            isStart = isSameDay(dayNorm, rangeStart);
            isEnd = isStart;
            inR = isStart;
          } else {
            const dTime = dayNorm.getTime();
            const sTime = rangeStart.getTime();
            const eTime = end.getTime();
            const min = Math.min(sTime, eTime);
            const max = Math.max(sTime, eTime);

            inR = dTime >= min && dTime <= max;
            isStart = dTime === min;
            isEnd = dTime === max;
          }
        }
      } else {
        isSelected = isSameDay(dayNorm, selectedDate);
      }

      return { isToday, isOtherMonth, isPast, disabledDay, inR, isStart, isEnd, isSelected };
    },
    [mode, rangeStart, rangeEnd, rangeHover, selectedDate, isDayDisabled],
  );

  const renderDay = (day: Date, currentMonthDate: Date) => {
    const { isToday, isOtherMonth, isPast, disabledDay, inR, isStart, isEnd, isSelected } = getDayProps(
      day,
      currentMonthDate,
    );

    return (
      <button
        type="button"
        key={day.toISOString()}
        disabled={disabledDay}
        className={classNames(
          s.dayCell,
          isOtherMonth && s.isOtherMonth,
          isPast && s.isPast,
          disabledDay && s.isDisabled,
          inR && s.inRange,
          isStart && s.rangeStart,
          isEnd && s.rangeEnd,
        )}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => {
          if (mode === 'range' && rangeStart) {
            setRangeHover(day);
          }
        }}
        onClick={() => {
          if (mode === 'range') handleRangeDayClick(day);
          else handleDaySelect(day);
        }}
      >
        <span className={classNames(s.dayText, isToday && s.isToday, isSelected && s.isSelected)}>{day.getDate()}</span>
      </button>
    );
  };

  const applyRange = () => {
    if (!rangeStart || !rangeEnd) return;
    const s = formatDateValue(rangeStart);
    const e = formatDateValue(rangeEnd);
    onChange?.(`${s}|${e}`);
    closeAll();
    onBlur?.();
  };

  const cancelRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setRangeHover(null);
    setRangeStep('start');
    closeAll();
    onBlur?.();
  };

  const renderRangeCalendar = (monthDays: Date[], monthDate: Date, isLeft: boolean) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isMonthOpen = isLeft ? isLeftMonthOpen : isRightMonthOpen;
    const setIsMonthOpen = isLeft ? setIsLeftMonthOpen : setIsRightMonthOpen;
    const isYearOpen = isLeft ? isLeftYearOpen : isRightYearOpen;
    const setIsYearOpen = isLeft ? setIsLeftYearOpen : setIsRightYearOpen;

    const areMonthsAdjacent = (() => {
      const lIdx = rangeLeftViewDate.getFullYear() * 12 + rangeLeftViewDate.getMonth();
      const rIdx = rangeRightViewDate.getFullYear() * 12 + rangeRightViewDate.getMonth();
      return rIdx === lIdx + 1;
    })();

    const currentYearOpts = getYearOptions(monthDate);

    return (
      <div className={s.rangeCalendar}>
        <div className={s.calendarHeader}>
          <button
            type="button"
            className={s.navBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => changeRangeMonth(isLeft, -1)}
            style={{ visibility: !isLeft && areMonthsAdjacent ? 'hidden' : 'visible' }}
          >
            <ChevronIcon className={classNames(s.chevron, s.chevronLeft)} />
          </button>

          <div className={s.monthYearGroup}>
            <div className={s.dropdownAnchor}>
              <button
                type="button"
                className={s.monthTitle}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setIsMonthOpen((p) => !p);
                  setIsYearOpen(false);
                  if (isLeft) {
                    setIsRightMonthOpen(false);
                    setIsRightYearOpen(false);
                  } else {
                    setIsLeftMonthOpen(false);
                    setIsLeftYearOpen(false);
                  }
                }}
              >
                {monthNames[monthDate.getMonth()]}
              </button>
              {isMonthOpen && (
                <div className={classNames(s.dropdown, s.monthDropdown)}>
                  {monthNames.map((m, idx) => {
                    const selected = idx === monthDate.getMonth();
                    return (
                      <button
                        type="button"
                        key={m}
                        className={classNames(s.dropdownItem, selected && s.dropdownItemSelected)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectRangeMonth(isLeft, idx)}
                      >
                        <span>{m}</span>
                        {selected && <CheckIcon />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={s.dropdownAnchor}>
              <button
                type="button"
                className={s.yearTitle}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setIsYearOpen((p) => !p);
                  setIsMonthOpen(false);
                  if (isLeft) {
                    setIsRightMonthOpen(false);
                    setIsRightYearOpen(false);
                  } else {
                    setIsLeftMonthOpen(false);
                    setIsLeftYearOpen(false);
                  }
                }}
              >
                {monthDate.getFullYear()}
              </button>
              {isYearOpen && (
                <div className={classNames(s.dropdown, s.yearDropdown)}>
                  {currentYearOpts.map((year) => {
                    const selected = year === monthDate.getFullYear();
                    return (
                      <button
                        type="button"
                        key={year}
                        className={classNames(s.dropdownItem, selected && s.dropdownItemSelected)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectRangeYear(isLeft, year)}
                      >
                        <span>{year}</span>
                        {selected && <CheckIcon />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className={s.navBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => changeRangeMonth(isLeft, 1)}
            style={{ visibility: isLeft && areMonthsAdjacent ? 'hidden' : 'visible' }}
          >
            <ChevronIcon className={classNames(s.chevron, s.chevronRight)} />
          </button>
        </div>

        <div className={s.weekRow}>
          {buildWeekDays().map((day) => (
            <span key={day} className={s.weekDay}>
              {day}
            </span>
          ))}
        </div>
        <div
          className={s.daysGrid}
          onMouseLeave={() => {
            if (rangeStart) {
              setRangeHover(null);
            }
          }}
        >
          {monthDays.map((day) => renderDay(day, monthDate))}
        </div>
      </div>
    );
  };

  return (
    <div className={s.root}>
      {renderTrigger ? (
        <div className={s.customTriggerWrap} ref={wrapperRef}>
          {renderTrigger(
            () => {
              if (!disabled) setIsOpen((p) => !p);
            },
            value || '',
            isOpen,
          )}
        </div>
      ) : (
        <>
          {label && <div className={s.labelRow}>{label}</div>}
          <div
            className={s.inputWrapper}
            ref={wrapperRef}
            onClick={() => {
              if (!disabled) {
                setIsOpen(true);
              }
            }}
          >
            {showCustomPlaceholder && placeholder && <span className={s.placeholder}>{placeholder}</span>}
            <input
              ref={inputRef}
              className={classNames(s.input, error && s.isError, showCustomPlaceholder && s.placeholderVisible)}
              type={type}
              name={name}
              value={value || ''}
              placeholder={appliedPlaceholder}
              onClick={(e) => {
                e.preventDefault();
              }}
              onChange={(e) => {
                onChange?.(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  if (e.key === 'Enter') {
                    if (isOpen) {
                      applyValue();
                    } else {
                      closeAll();
                    }
                  }
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                if (value) {
                  const yearClamped = clampYear(value);
                  const rangeClamped = clampToRange(yearClamped);
                  if (rangeClamped !== value) {
                    onChange?.(rangeClamped);
                  }
                }
                onBlur?.();
              }}
              min={minProp}
              max={maxProp}
              disabled={disabled}
            />
            <button
              type="button"
              className={s.iconButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
                setIsMonthOpen(false);
                setIsYearOpen(false);
                openPicker();
              }}
              aria-label="Выбрать дату или время"
              disabled={disabled}
            >
              {icon}
            </button>
          </div>
        </>
      )}
      {error && error.trim().length > 0 && <span className={s.error}>{error}</span>}

      {isOpen && !disabled && mode === 'range' && (
        <Portal>
          <div
            className={s.rangePopup}
            ref={popupRef}
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              zIndex: 100000,
            }}
          >
            <div className={s.rangeBody}>
              {renderRangeCalendar(rangeLeftDays, rangeLeftViewDate, true)}
              {renderRangeCalendar(rangeRightDays, rangeRightViewDate, false)}
            </div>
            <div className={s.rangeFooter}>
              <button
                type="button"
                className={s.cancelBtn}
                onClick={cancelRange}
                onMouseDown={(e) => e.preventDefault()}
              >
                Отмена
              </button>
              <button
                type="button"
                className={classNames(s.applyBtn, s.rangeApply)}
                disabled={!rangeStart || !rangeEnd}
                onClick={applyRange}
                onMouseDown={(e) => e.preventDefault()}
              >
                Ок
              </button>
            </div>
          </div>
        </Portal>
      )}

      {isOpen && !disabled && mode !== 'range' && (
        <Portal>
          <div
            className={classNames(s.popup, timeOnly && s.timeOnly)}
            ref={popupRef}
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              zIndex: 100000,
            }}
          >
            <div className={s.popupBody}>
              {mode !== 'time' && (
                <div className={s.calendar}>
                  <div className={s.calendarHeader}>
                    <button
                      type="button"
                      className={s.navBtn}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => changeMonth(-1)}
                      aria-label="Предыдущий месяц"
                    >
                      <ChevronIcon className={classNames(s.chevron, s.chevronLeft)} />
                    </button>
                    <div className={s.monthYearGroup}>
                      <div className={s.dropdownAnchor}>
                        <button
                          type="button"
                          className={s.monthTitle}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setIsMonthOpen((p) => !p);
                            setIsYearOpen(false);
                          }}
                        >
                          {monthNames[viewDate.getMonth()]}
                        </button>
                        {isMonthOpen && (
                          <div className={classNames(s.dropdown, s.monthDropdown)}>
                            {monthNames.map((m, idx) => {
                              const selected = idx === viewDate.getMonth();
                              return (
                                <button
                                  type="button"
                                  key={m}
                                  className={classNames(s.dropdownItem, selected && s.dropdownItemSelected)}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectMonth(idx)}
                                >
                                  <span>{m}</span>
                                  {selected && <CheckIcon />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className={s.dropdownAnchor}>
                        <button
                          type="button"
                          className={s.yearTitle}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setIsYearOpen((p) => !p);
                            setIsMonthOpen(false);
                          }}
                        >
                          {viewDate.getFullYear()}
                        </button>
                        {isYearOpen && (
                          <div className={classNames(s.dropdown, s.yearDropdown)}>
                            {yearOptions.map((year) => {
                              const selected = year === viewDate.getFullYear();
                              return (
                                <button
                                  type="button"
                                  key={year}
                                  className={classNames(s.dropdownItem, selected && s.dropdownItemSelected)}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectYear(year)}
                                >
                                  <span>{year}</span>
                                  {selected && <CheckIcon />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={s.navBtn}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => changeMonth(1)}
                      aria-label="Следующий месяц"
                    >
                      <ChevronIcon className={classNames(s.chevron, s.chevronRight)} />
                    </button>
                  </div>
                  <div className={s.weekRow}>
                    {buildWeekDays().map((day) => (
                      <span key={day} className={s.weekDay}>
                        {day}
                      </span>
                    ))}
                  </div>
                  <div className={s.daysGrid}>{days.map((day) => renderDay(day, viewDate))}</div>
                </div>
              )}

              {mode !== 'date' && (
                <div className={s.timePane}>
                  <div className={s.timeHeader}>{displayTime}</div>
                  <div className={s.timeColumns}>
                    <div className={s.timeColumn}>
                      {[...Array(24)].map((_, idx) => (
                        <button
                          type="button"
                          key={idx}
                          className={classNames(s.timeItem, selectedHour === idx && s.timeItemActive)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTimeSelect(idx)}
                        >
                          {pad(idx)}
                        </button>
                      ))}
                    </div>
                    <div className={s.divider} />
                    <div className={s.timeColumn}>
                      {[...Array(60)].map((_, idx) => (
                        <button
                          type="button"
                          key={idx}
                          className={classNames(s.timeItem, selectedMinute === idx && s.timeItemActive)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTimeSelect(selectedHour, idx)}
                        >
                          {pad(idx)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={s.footer}>
              {onClear && value && (
                <button
                  type="button"
                  className={s.clearBtn}
                  onClick={() => {
                    onClear();
                    setIsOpen(false);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  Очистить
                </button>
              )}
              <button type="button" className={s.applyBtn} onClick={applyValue} onMouseDown={(e) => e.preventDefault()}>
                Ок
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
